import { defineComponent as _defineComponent } from 'vue';
import { resolveDynamicComponent as _resolveDynamicComponent, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, unref as _unref, createVNode as _createVNode, withCtx as _withCtx, createElementBlock as _createElementBlock, mergeProps as _mergeProps } from "vue";
const _hoisted_1 = { class: "grid min-h-full grid-cols-[auto_1fr] bg-white" };
const _hoisted_2 = {
    class: "px-5 pt-6 pb-4 flex flex-col min-w-0 min-h-0",
    "data-testid": "users-list-view"
};
const _hoisted_3 = { class: "flex items-center justify-between mb-6 sm:mb-8" };
const _hoisted_4 = {
    class: "text-2xl sm:text-3xl font-bold text-(--colors-text-heading)",
    "data-testid": "users-list-title"
};
const _hoisted_5 = {
    key: 0,
    role: "alert",
    class: "mb-4 rounded border border-(--colors-border-error-subtle) bg-(--colors-background-error) px-4 py-3 text-sm text-(--colors-text-error)",
    "data-testid": "users-list-error"
};
import { computed, h, onMounted, ref, shallowRef, watch } from "vue";
import { watchDebounced } from "@vueuse/core";
import { useI18n } from "vue-i18n";
import { RouterLink } from "vue-router";
import { Avatar, DataTable, Pager, TableShell, TableFilterSelector, Button, Dropdown, Tooltip, useTableQuery, useTableViews, } from "@eudonet/opal";
import { useTableQueryUrlSync } from "@/shared/composables/useTableQueryUrlSync";
import { useActiveViewFilters } from "@/shared/composables/useActiveViewFilters";
import { buildTableLabels } from "@/shared/composables/tableLabels";
import { buildFilterSelectorLabels } from "@/shared/composables/filterSelectorLabels";
import { resolveApiErrorKey } from "@/infrastructure/services/ApiErrorService";
import { displayDateToLocalMidnightIso, toDatePickerFormat } from "@/shared/utils/date-formatting";
import { useListRowSelection } from "@/shared/composables/useListRowSelection";
import ListFloatActionBar from "@/shared/components/ListFloatActionBar.vue";
import { logger } from "@/infrastructure/services/LoggerService";
import { moduleRegistry } from "@/router/moduleRegistry";
import { formatDateTime } from "@/shared/utils/date-formatting";
import { useProfileStore } from "@/modules/settings/public";
import { useUsersAdminStore } from "../../stores/useUsersAdminStore";
import { useUsersListColumnSettings } from "../composables/useUsersListColumnSettings";
import { useUserActions } from "../composables/useUserActions";
import { RolesService } from "../../infrastructure/adapters/RolesService";
import { EnvironmentsService } from "../../infrastructure/adapters/EnvironmentsService";
import UserStateBadge from "../components/UserStateBadge.vue";
import UserRolesCell from "../components/UserRolesCell.vue";
import UserActionConfirmModal from "../components/UserActionConfirmModal.vue";
import InviteUserModal from "../components/InviteUserModal.vue";
import { environmentLabel } from "../../domain/entities/UserListItem";
import { isSelectableRole } from "../../domain/entities/Role";
const LOAD_ERROR_FALLBACK = "accessManager.users.loadFailed";
export default /*@__PURE__*/ _defineComponent({
    __name: 'UsersListView',
    setup(__props) {
        const SidebarComponent = shallowRef(moduleRegistry.getComponentForSlot("sidebar"));
        const ROW_OPTIONS = [10, 30, 50];
        const SORT_FIELD_BY_COLUMN = {
            name: "Name",
            email: "Email",
            state: "State",
            lastLogin: "LastLogin",
        };
        const USERS_LIST_STATES = ["Active", "Inactive", "Invited"];
        // Same wording as the State column badge (UserStateBadge).
        const STATE_LABEL_KEYS = {
            Active: "active",
            Inactive: "inactive",
            Invited: "pending",
        };
        const { t, locale } = useI18n();
        const store = useUsersAdminStore();
        const profileStore = useProfileStore();
        const dateFormat = computed(() => profileStore.profile?.dateFormat);
        const timeFormat = computed(() => profileStore.profile?.timeFormat);
        const { columnSettings } = useUsersListColumnSettings();
        const presetViews = [
            {
                id: "all",
                label: t("accessManager.users.tabs.all"),
                isPreset: true,
                isDefault: true,
                querySnapshot: {},
            },
        ];
        const tableQuery = useTableQuery({
            pageSize: ROW_OPTIONS[0],
            activeViewId: "all",
            filterLabels: () => filterLabels.value,
            filterItems: () => ({
                roles: roleItems.value,
                environments: environmentItems.value,
                state: stateItems.value,
            }),
            filterFieldConfigs: () => ({
                name: { placeholder: t("accessManager.users.filters.namePlaceholder") },
                email: { placeholder: t("accessManager.users.filters.emailPlaceholder") },
                lastLogin: { dateRangeConfig: dateRangeConfig.value },
            }),
        });
        const tableLabels = computed(() => buildTableLabels(t));
        // Row / header user actions (deactivate, reset password, resend invite, …). On success
        // re-fetch the current page so the State badge + row reflect the new state (fetchUsers is
        // a hoisted function declaration below).
        const userActions = useUserActions({ onActionSuccess: () => fetchUsers() });
        const { canManageUsers, buildActionsFor, isConfirmOpen, confirmTitleKey, confirmBodyKey, confirmLabelKey, confirmDanger, isRunning, confirm: onConfirmAction, cancel: onCancelAction, } = userActions;
        const showInviteModal = ref(false);
        const tableViews = useTableViews({ initialViews: presetViews });
        useActiveViewFilters(tableQuery, tableViews);
        // The preset label is a snapshot taken at setup; relabel it or a language switch leaves it stale.
        watch(locale, () => tableViews.updateView("all", { label: t("accessManager.users.tabs.all") }));
        // Persist page + page size + search in the URL (deep-link / refresh survives). Runs
        // before the load watch below so the first load uses the hydrated page/search.
        useTableQueryUrlSync({ tableQuery, syncSearch: true, defaultPageSize: ROW_OPTIONS[0] });
        // `select` is prepended by this view (checkbox column, not part of the persisted
        // column-settings order) — mirrors CampaignListView/OrganisationList.
        const columnOrder = computed(() => ["select", ...columnSettings.order.value, "actions"]);
        const columnPinning = computed(() => ({
            left: ["select", ...(columnSettings.pinning.value.left ?? []).filter((k) => k !== "select")],
            right: ["actions", ...(columnSettings.pinning.value.right ?? []).filter((k) => k !== "actions")],
        }));
        // ── Column filters ───────────────────────────────────────────────────────────
        const roleItems = ref([]);
        const environmentItems = ref([]);
        // The options only feed the Roles and Environments filters; failing to load them must not
        // break the list, so each failure is logged (R47) and leaves that filter with no values.
        onMounted(() => {
            new RolesService()
                .getAll()
                .then((roles) => {
                roleItems.value = roles.filter((r) => isSelectableRole(r.type)).map((r) => ({ id: r.type, label: r.label }));
            })
                .catch((err) => logger.error("Failed to load the users list role filter options", err));
            new EnvironmentsService()
                .getAll()
                .then((environments) => {
                environmentItems.value = environments.map((e) => ({ id: String(e.id), label: e.label }));
            })
                .catch((err) => logger.error("Failed to load the users list environment filter options", err));
        });
        const stateItems = computed(() => USERS_LIST_STATES.map((state) => ({ id: state, label: t(`accessManager.users.state.${STATE_LABEL_KEYS[state]}`) })));
        const dateRangeConfig = computed(() => ({
            format: toDatePickerFormat(profileStore.profile?.dateFormat, locale.value),
            fromLabel: t("accessManager.users.filters.dateFrom"),
            toLabel: t("accessManager.users.filters.dateTo"),
            placeholder: t("accessManager.users.filters.datePlaceholder"),
        }));
        const filterLabels = computed(() => ({
            name: t("accessManager.users.columns.name"),
            email: t("accessManager.users.columns.email"),
            roles: t("accessManager.users.columns.roles"),
            environments: t("accessManager.users.columns.environments"),
            state: t("accessManager.users.columns.state"),
            lastLogin: t("accessManager.users.columns.lastLogin"),
        }));
        const filterSelectorLabels = computed(() => buildFilterSelectorLabels(t));
        const filterSelectorRef = ref(null);
        const filterFields = computed(() => [
            {
                key: "name",
                label: filterLabels.value.name,
                icon: "ri-user-search-line",
                type: "text",
                placeholder: t("accessManager.users.filters.namePlaceholder"),
            },
            {
                key: "email",
                label: filterLabels.value.email,
                icon: "ri-mail-line",
                type: "text",
                placeholder: t("accessManager.users.filters.emailPlaceholder"),
            },
            {
                key: "roles",
                label: filterLabels.value.roles,
                icon: "ri-shield-user-line",
                type: "list",
                items: roleItems.value,
                selectAllLabel: t("table.selectAll"),
                step2SearchPlaceholder: t("accessManager.users.filters.searchValue"),
            },
            {
                key: "environments",
                label: filterLabels.value.environments,
                icon: "ri-stack-line",
                type: "list",
                items: environmentItems.value,
                selectAllLabel: t("table.selectAll"),
                step2SearchPlaceholder: t("accessManager.users.filters.searchValue"),
            },
            {
                key: "state",
                label: filterLabels.value.state,
                icon: "ri-toggle-line",
                type: "list",
                items: stateItems.value,
                selectAllLabel: t("table.selectAll"),
                step2SearchPlaceholder: t("accessManager.users.filters.searchValue"),
            },
            {
                key: "lastLogin",
                label: filterLabels.value.lastLogin,
                icon: "ri-calendar-line",
                type: "dateRange",
                dateFormat: dateRangeConfig.value.format,
                datePlaceholder: dateRangeConfig.value.placeholder,
                dateFromLabel: dateRangeConfig.value.fromLabel,
                dateToLabel: dateRangeConfig.value.toLabel,
            },
        ]);
        function handleListFilterApply(key, values) {
            if (values.length > 0)
                tableQuery.setFilter(key, { type: "in", values });
            else
                tableQuery.removeFilter(key);
        }
        function localMidnightIso(display, dayOffset) {
            return displayDateToLocalMidnightIso(display, profileStore.profile?.dateFormat, locale.value, dayOffset);
        }
        function textFilter(key) {
            const value = tableQuery.state.filters[key];
            return value?.type === "equals" ? String(value.value).trim() || undefined : undefined;
        }
        function listFilter(key) {
            const value = tableQuery.state.filters[key];
            return value?.type === "in" && value.values.length > 0 ? [...value.values] : undefined;
        }
        // The last-login range is a whole-day range in the viewer's time zone: from midnight of the
        // "from" day (inclusive) to midnight AFTER the "to" day (exclusive), sent as UTC instants (R51).
        function wireFilters() {
            const lastLogin = tableQuery.state.filters.lastLogin;
            const range = lastLogin?.type === "dateRange" ? lastLogin : undefined;
            return {
                name: textFilter("name"),
                email: textFilter("email"),
                roleTypes: listFilter("roles"),
                states: listFilter("state"),
                environmentIds: listFilter("environments")?.map(Number),
                lastLoginFrom: range ? localMidnightIso(range.from, 0) : undefined,
                lastLoginBefore: range ? localMidnightIso(range.to, 1) : undefined,
            };
        }
        // R70: the user-fixable code gets its own message; anything else stays the generic load error.
        const LOAD_ERROR_KEYS = {
            "UsersList.LastLogin.InvalidRange": "accessManager.users.filters.dateRangeError",
        };
        const loadErrorKey = ref(LOAD_ERROR_FALLBACK);
        const sortItems = (columnId) => [
            { label: t("table.sortAZ"), onClick: () => tableQuery.setSort(columnId, "asc") },
            { label: t("table.sortZA"), onClick: () => tableQuery.setSort(columnId, "desc") },
        ];
        const { selectColumn, effectiveCount, allPagesSelected, totalAcrossAllPages, clearSelection, onSelectAllPages } = useListRowSelection({
            t,
            rows: () => store.users,
            getRowId: (user) => user.id,
            totalCount: () => store.totalItems,
        });
        const columns = computed(() => [
            selectColumn,
            {
                id: "name",
                header: t("accessManager.users.columns.name"),
                cell: ({ row }) => h("div", { class: "flex items-center gap-2 min-w-0" }, [
                    h(Avatar, { name: row.original.name, class: "shrink-0", presentational: true }),
                    h(RouterLink, {
                        to: { name: "AccessManagerUserDetail", params: { userId: row.original.id } },
                        class: "text-(--colors-text-link) hover:underline",
                    }, () => row.original.name),
                ]),
                headerMenuItems: [...sortItems("name")],
            },
            {
                id: "email",
                header: t("accessManager.users.columns.email"),
                cell: ({ row }) => row.original.email,
                headerMenuItems: [...sortItems("email")],
            },
            {
                id: "roles",
                header: t("accessManager.users.columns.roles"),
                cell: ({ row }) => h(UserRolesCell, { roles: row.original.roles }),
            },
            {
                id: "environments",
                header: t("accessManager.users.columns.environments"),
                cell: ({ row }) => row.original.environments.map(environmentLabel).join(", ") || "–",
            },
            {
                id: "state",
                header: t("accessManager.users.columns.state"),
                cell: ({ row }) => h(UserStateBadge, { enabled: row.original.enabled, isInvited: row.original.isInvited }),
                headerMenuItems: [...sortItems("state")],
            },
            {
                id: "lastLogin",
                header: t("accessManager.users.columns.lastLogin"),
                cell: ({ row }) => formatDateTime(row.original.lastLogin ?? undefined, locale.value, dateFormat.value, timeFormat.value),
                headerMenuItems: [...sortItems("lastLogin")],
            },
            {
                // Per-row (…) kebab of admin actions (deactivate/reactivate, reset password, resend
                // invite). Items are state- and permission-filtered by the composable; an empty set
                // (no rights) renders nothing rather than an empty menu.
                id: "actions",
                header: "",
                cell: ({ row }) => {
                    const items = buildActionsFor(row.original);
                    return items.length
                        ? h(Dropdown, {
                            ghost: true,
                            label: "",
                            triggerIconLeft: "ri-more-line",
                            menuAlign: "right",
                            ariaLabel: t("accessManager.users.rowActions"),
                            items,
                        })
                        : h("span");
                },
                draggable: false,
                resizable: false,
                width: 56,
                minWidth: 56,
                maxWidth: 56,
            },
        ]);
        // ── Data loading ──────────────────────────────────────────────────────────────
        // Gate on column-settings finishing their load, mirroring Contact's `dataReady` — the
        // first fetch should carry any restored column state before hitting the BFF.
        // What the last fetch sent; a different state.search means a debounce is pending.
        let fetchedSearch = tableQuery.state.search;
        let fetchedPage = tableQuery.state.page;
        function fetchUsers() {
            if (columnSettings.isLoading.value)
                return;
            fetchedSearch = tableQuery.state.search;
            fetchedPage = tableQuery.state.page;
            const s = tableQuery.state.sort;
            const sortBy = s?.column ? SORT_FIELD_BY_COLUMN[s.column] : undefined;
            const sortDirection = s?.direction
                ? s.direction === "asc"
                    ? "Ascending"
                    : "Descending"
                : undefined;
            const useSort = Boolean(sortBy && sortDirection);
            // Rejection is flagged in store.error and surfaced by the alert above, with the message
            // resolved here from the BFF errorCode.
            store
                .loadUsers({
                pageNumber: tableQuery.state.page ?? 1,
                pageSize: tableQuery.state.pageSize ?? ROW_OPTIONS[0],
                sortBy: useSort ? sortBy : undefined,
                sortDirection: useSort ? sortDirection : undefined,
                search: tableQuery.state.search || undefined,
                ...wireFilters(),
            })
                .then((applied) => {
                if (applied)
                    loadErrorKey.value = LOAD_ERROR_FALLBACK;
            })
                .catch((e) => {
                loadErrorKey.value = resolveApiErrorKey(e, LOAD_ERROR_KEYS, LOAD_ERROR_FALLBACK);
            });
        }
        // Filters share this watcher with paging: setFilter/removeFilter also reset the page, so a
        // separate filters watcher would fetch twice for one change. OPAL's pill ✕ and "Erase all"
        // mutate state.filters directly and emit nothing, so only an observer hears them.
        // CONSTRAINT: TableShell's search calls setSearch, which also resets the page. That page
        // change lands here before the search debounce settles, so it is skipped and the debounced
        // watcher below makes the one fetch; dropping the check fetches a half-typed term too.
        watch(() => ({
            page: tableQuery.state.page,
            pageSize: tableQuery.state.pageSize,
            sort: tableQuery.state.sort,
            filters: tableQuery.state.filters,
        }), () => {
            if (tableQuery.state.search !== fetchedSearch)
                return;
            fetchUsers();
        }, { immediate: true, deep: true });
        // Column settings finish loading asynchronously (persisted state fetch) — once they
        // do, run the real first fetch (the immediate run above was a no-op while loading).
        watch(columnSettings.isLoading, (loading) => {
            if (!loading)
                fetchUsers();
        });
        // Debounced search (300ms). setSearch has already moved to page 1, so this only fetches. The
        // page check covers a term typed and erased within the window: the skipped page reset still
        // needs its load.
        watchDebounced(() => tableQuery.state.search, () => {
            if (tableQuery.state.search !== fetchedSearch || tableQuery.state.page !== fetchedPage)
                fetchUsers();
        }, { debounce: 300 });
        return (_ctx, _cache) => {
            return (_openBlock(), _createElementBlock("div", _hoisted_1, [
                (SidebarComponent.value)
                    ? (_openBlock(), _createBlock(_resolveDynamicComponent(SidebarComponent.value), { key: 0 }))
                    : _createCommentVNode("v-if", true),
                _createElementVNode("main", _hoisted_2, [
                    _createElementVNode("div", _hoisted_3, [
                        _createElementVNode("h1", _hoisted_4, _toDisplayString(_ctx.$t("accessManager.users.title")), 1 /* TEXT */),
                        _createVNode(_unref(Tooltip), {
                            text: _unref(t)('common.permissions.noEditRights'),
                            disabled: _unref(canManageUsers),
                            tabindex: _unref(canManageUsers) ? undefined : 0
                        }, {
                            default: _withCtx(() => [
                                _createVNode(_unref(Button), {
                                    variant: "primary",
                                    "icon-left": "ri-user-add-line",
                                    label: _ctx.$t('accessManager.users.actions.invite'),
                                    disabled: !_unref(canManageUsers),
                                    "data-testid": "users-invite-button",
                                    onClick: _cache[0] || (_cache[0] = ($event) => (showInviteModal.value = true))
                                }, null, 8 /* PROPS */, ["label", "disabled"])
                            ]),
                            _: 1 /* STABLE */
                        }, 8 /* PROPS */, ["text", "disabled", "tabindex"])
                    ]),
                    (_unref(store).error)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_5, _toDisplayString(_ctx.$t(loadErrorKey.value)), 1 /* TEXT */))
                        : _createCommentVNode("v-if", true),
                    _createVNode(_unref(TableShell), {
                        query: _unref(tableQuery),
                        views: _unref(tableViews),
                        "column-settings": _unref(columnSettings),
                        labels: tableLabels.value,
                        permissions: { canSaveView: false, canShareView: false },
                        "show-view-selector": true,
                        "show-search": true,
                        "show-filter": true,
                        "search-placeholder": _ctx.$t('accessManager.users.searchPlaceholder'),
                        onEditFilter: _cache[3] || (_cache[3] = (key) => filterSelectorRef.value?.openForField(key))
                    }, {
                        filter: _withCtx(() => [
                            _createVNode(_unref(TableFilterSelector), _mergeProps(filterSelectorLabels.value, {
                                ref_key: "filterSelectorRef",
                                ref: filterSelectorRef,
                                fields: filterFields.value,
                                "active-filters": _unref(tableQuery).state.filters,
                                "trigger-label": _unref(t)('table.filter'),
                                "trigger-icon-left": "ri-filter-2-line",
                                "active-count": _unref(tableQuery).activeFilterCount.value,
                                "step1-search-placeholder": _unref(t)('accessManager.users.filters.searchField'),
                                "confirm-label": _unref(t)('table.tabs.apply'),
                                "back-label": _unref(t)('accessManager.users.filters.back'),
                                "advanced-filters-label": "",
                                "no-results-label": _unref(t)('accessManager.users.filters.noResults'),
                                "date-range-error-label": _unref(t)('accessManager.users.filters.dateRangeError'),
                                onApply: handleListFilterApply,
                                onApplyFilter: _cache[1] || (_cache[1] = (key, value) => _unref(tableQuery).setFilter(key, value)),
                                onRemoveFilter: _cache[2] || (_cache[2] = (key) => _unref(tableQuery).removeFilter(key))
                            }), null, 16 /* FULL_PROPS */, ["fields", "active-filters", "trigger-label", "active-count", "step1-search-placeholder", "confirm-label", "back-label", "no-results-label", "date-range-error-label"])
                        ]),
                        table: _withCtx(() => [
                            _createVNode(_unref(DataTable), {
                                columns: columns.value,
                                data: _unref(store).users,
                                loading: _unref(store).isLoading,
                                "column-visibility": _unref(columnSettings).visibility,
                                "column-order": columnOrder.value,
                                "column-pinning": columnPinning.value,
                                "column-sizing": _unref(columnSettings).widths.value,
                                "has-settings": "",
                                "sticky-header": "",
                                "empty-text": _ctx.$t('accessManager.users.empty'),
                                onSettingsClick: _unref(columnSettings).openDrawer,
                                "onUpdate:columnSizing": _unref(columnSettings).handleSizingChange
                            }, null, 8 /* PROPS */, ["columns", "data", "loading", "column-visibility", "column-order", "column-pinning", "column-sizing", "empty-text", "onSettingsClick", "onUpdate:columnSizing"])
                        ]),
                        pagination: _withCtx(() => [
                            _createVNode(_unref(Pager), {
                                "current-page": _unref(store).currentPage,
                                "total-pages": _unref(store).totalPages,
                                "total-items": _unref(store).totalItems,
                                "items-per-page": _unref(tableQuery).state.pageSize,
                                "rows-per-page-options": ROW_OPTIONS,
                                "rows-per-page-label": _ctx.$t('pagination.rowsPerPage'),
                                "items-count-separator": _ctx.$t('pagination.of'),
                                "previous-label": _ctx.$t('pagination.previous'),
                                "next-label": _ctx.$t('pagination.next'),
                                "show-go-to-page": "",
                                "go-to-page-label": _ctx.$t('pagination.goToPage'),
                                "go-label": _ctx.$t('pagination.go'),
                                onPageChange: _unref(tableQuery).setPage,
                                onItemsPerPageChange: _unref(tableQuery).setPageSize
                            }, null, 8 /* PROPS */, ["current-page", "total-pages", "total-items", "items-per-page", "rows-per-page-label", "items-count-separator", "previous-label", "next-label", "go-to-page-label", "go-label", "onPageChange", "onItemsPerPageChange"])
                        ]),
                        _: 1 /* STABLE */
                    }, 8 /* PROPS */, ["query", "views", "column-settings", "labels", "search-placeholder"]),
                    _createVNode(ListFloatActionBar, {
                        "selection-count": _unref(effectiveCount),
                        "all-pages-selected": _unref(allPagesSelected),
                        "total-across-all-pages": _unref(totalAcrossAllPages),
                        onDismiss: _unref(clearSelection),
                        onSelectAllPages: _unref(onSelectAllPages)
                    }, null, 8 /* PROPS */, ["selection-count", "all-pages-selected", "total-across-all-pages", "onDismiss", "onSelectAllPages"]),
                    _createVNode(UserActionConfirmModal, {
                        "model-value": _unref(isConfirmOpen),
                        "title-key": _unref(confirmTitleKey),
                        "body-key": _unref(confirmBodyKey),
                        "confirm-key": _unref(confirmLabelKey),
                        danger: _unref(confirmDanger),
                        loading: _unref(isRunning),
                        onAccept: _unref(onConfirmAction),
                        onCancel: _unref(onCancelAction)
                    }, null, 8 /* PROPS */, ["model-value", "title-key", "body-key", "confirm-key", "danger", "loading", "onAccept", "onCancel"]),
                    _createVNode(InviteUserModal, {
                        "model-value": showInviteModal.value,
                        onClose: _cache[4] || (_cache[4] = ($event) => (showInviteModal.value = false)),
                        onCreated: fetchUsers
                    }, null, 8 /* PROPS */, ["model-value"])
                ])
            ]));
        };
    }
});
